import React, { useState, useEffect } from 'react';
import SettingsPanel from './cesium/components/SettingsPanel';
import AttributeDisplay from './cesium/components/AttributeDisplay';

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface BimModel {
  assetId: number;
  assetName: string;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
  styles?: StyleCondition[];
}

interface CesiumControlsProps {
  bimModels: BimModel[];
  onBimModelsChange: (models: BimModel[]) => void;
}

const CesiumControls: React.FC<CesiumControlsProps> = ({ 
  bimModels, 
  onBimModelsChange 
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState<{ [key: string]: any } | null>(null);
  const [isAttributeDisplayVisible, setIsAttributeDisplayVisible] = useState(false);

  // Collect all style conditions from all assets
  const getAllStyleConditions = (): StyleCondition[] => {
    return bimModels.flatMap(model => model.styles || []);
  };

  const handleDepthTestChange = (enabled: boolean) => {
    window.dispatchEvent(new CustomEvent('depthTestChange', { detail: { enabled } }));
  };

  const handleShadowsChange = (enabled: boolean) => {
    window.dispatchEvent(new CustomEvent('shadowsChange', { detail: { enabled } }));
  };

  const findAssetForProperty = (property: string, attributes: { [key: string]: any }): BimModel | null => {
    // Try to find the asset that contains this property
    // For now, we'll use a simple heuristic but this could be improved
    // by analyzing the property structure or asset metadata
    
    // Check if we can match by asset name or other identifying features
    const propertyString = property.toLowerCase();
    
    for (const model of bimModels) {
      // If the property name contains any part of the asset name, assign it to that asset
      if (propertyString.includes(model.assetName.toLowerCase()) ||
          model.assetName.toLowerCase().includes(propertyString)) {
        return model;
      }
    }
    
    // If no specific match found, assign to the first asset
    return bimModels[0] || null;
  };

  const handleStyleConditionsChange = (conditions: StyleCondition[]) => {
    // Distribute conditions to appropriate assets based on property analysis
    const updatedModels: BimModel[] = bimModels.map(model => ({
      ...model,
      styles: [] as StyleCondition[]
    }));

    // Group conditions by asset
    conditions.forEach(condition => {
      const targetAsset = findAssetForProperty(condition.property, {});
      if (targetAsset) {
        const modelIndex = updatedModels.findIndex(m => m.assetId === targetAsset.assetId);
        if (modelIndex >= 0 && updatedModels[modelIndex].styles) {
          updatedModels[modelIndex].styles!.push(condition);
        }
      }
    });

    // If no asset was found for some conditions, put them on the first asset
    const orphanConditions = conditions.filter(condition => {
      return !updatedModels.some(model => 
        model.styles && model.styles.some(s => s.property === condition.property && s.value === condition.value)
      );
    });
    
    if (orphanConditions.length > 0 && updatedModels.length > 0 && updatedModels[0].styles) {
      updatedModels[0].styles.push(...orphanConditions);
    }

    onBimModelsChange(updatedModels);
    
    window.dispatchEvent(new CustomEvent('styleConditionsChange', { 
      detail: { conditions } 
    }));
  };

  const handleAttributeSelect = (property: string, value: any) => {
    const newCondition: StyleCondition = {
      property,
      value: String(value),
      color: '#ffffff',
      opacity: 1.0,
    };

    const currentConditions = getAllStyleConditions();
    const conditionExists = currentConditions.some(
      c => c.property === newCondition.property && c.value === newCondition.value
    );

    if (!conditionExists) {
      const updatedConditions = [...currentConditions, newCondition];
      handleStyleConditionsChange(updatedConditions);
    }
    
    setIsAttributeDisplayVisible(false);
  };

  const handleCloseAttributeDisplay = () => {
    setIsAttributeDisplayVisible(false);
  };

  const handleObjectClick = (attributes: { [key: string]: any }) => {
    setSelectedAttributes(attributes);
    setIsAttributeDisplayVisible(true);
  };

  // Set up event listeners for cesium object clicks
  useEffect(() => {
    const handleCesiumObjectClick = (event: CustomEvent) => {
      handleObjectClick(event.detail.attributes || event.detail);
    };

    const handleCesiumEmptyClick = () => {
      // Close AttributeDisplay when clicking on empty space or non-BIM objects
      setIsAttributeDisplayVisible(false);
    };

    // Listen for various cesium click events
    window.addEventListener('cesiumObjectClick', handleCesiumObjectClick as EventListener);
    window.addEventListener('objectClick', handleCesiumObjectClick as EventListener);
    window.addEventListener('cesiumEmptyClick', handleCesiumEmptyClick as EventListener);
    
    // Also dispatch a ready event so cesium knows we're listening
    window.dispatchEvent(new CustomEvent('cesiumControlsReady'));
    
    return () => {
      window.removeEventListener('cesiumObjectClick', handleCesiumObjectClick as EventListener);
      window.removeEventListener('objectClick', handleCesiumObjectClick as EventListener);
      window.removeEventListener('cesiumEmptyClick', handleCesiumEmptyClick as EventListener);
    };
  }, []);

  return (
    <>
      <SettingsPanel
        onDepthTestChange={handleDepthTestChange}
        onShadowsChange={handleShadowsChange}
        conditions={getAllStyleConditions()}
        onStyleConditionsChange={handleStyleConditionsChange}
      />
      <AttributeDisplay
        attributes={selectedAttributes}
        visible={isAttributeDisplayVisible}
        onClose={handleCloseAttributeDisplay}
        onAttributeClick={handleAttributeSelect}
      />
    </>
  );
};

export default CesiumControls; 